<template>
  <div class="ls">

    <!-- ══════════════ LEFT BRAND PANEL ══════════════ -->
    <aside class="ls-left">
      <!-- Ambient glow orbs -->
      <div class="ls-orb ls-orb--a" />
      <div class="ls-orb ls-orb--b" />
      <div class="ls-orb ls-orb--c" />
      <!-- Decorative concentric rings -->
      <div class="ls-ring ls-ring--1" />
      <div class="ls-ring ls-ring--2" />
      <div class="ls-ring ls-ring--3" />

      <div class="ls-left__inner">
        <!-- Logo & brand -->
        <div class="ls-brand">
          <div class="ls-brand__logo-shell">
            <img
              :src="require('@/assets/logo/icon-geebung.png')"
              alt="Salamanda"
              class="ls-brand__logo"
            />
          </div>
          <h1 class="ls-brand__name">
            Salamanda<span class="ls-brand__accent"> ERP</span>
          </h1>
          <p class="ls-brand__tagline">{{ $t('login.system_description') }}</p>
        </div>

        <!-- Feature highlights -->
        <ul class="ls-feats">
          <li v-for="feat in loginFeatures" :key="feat.key" class="ls-feat">
            <div class="ls-feat__icon">
              <i :class="['mdi', feat.icon]" />
            </div>
            <div class="ls-feat__body">
              <strong class="ls-feat__title">{{ $t(feat.titleKey) }}</strong>
              <span class="ls-feat__hint">{{ $t(feat.hintKey) }}</span>
            </div>
          </li>
        </ul>
      </div>

      <p class="ls-left__copy">{{ $t('login.copyright') }}</p>
    </aside>

    <!-- ══════════════ RIGHT FORM PANEL ══════════════ -->
    <main class="ls-right">

      <!-- Server menu (top-right corner) -->
      <div ref="serverMenuRoot" class="ls-sm">
        <button
          type="button"
          class="ls-sm__btn"
          aria-label="Server settings"
          @click="toggleServerMenu"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="ls-sm__icon">
            <path d="M10 6.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM10 11.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3ZM10 16.5a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3Z" />
          </svg>
        </button>
        <div v-if="showServerMenu" class="ls-sm__menu">
          <button type="button" class="ls-sm__item" @click="changeServerAddress">
            <span class="ls-sm__item-icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M4 6.75A3.75 3.75 0 0 1 7.75 3h8.5A3.75 3.75 0 0 1 20 6.75v1.5A3.75 3.75 0 0 1 16.25 12h-8.5A3.75 3.75 0 0 1 4 8.25v-1.5Zm3.75-.75a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H7.75Zm3 0a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H10.75Z" />
                <path d="M4 15.75A3.75 3.75 0 0 1 7.75 12h8.5A3.75 3.75 0 0 1 20 15.75v1.5A3.75 3.75 0 0 1 16.25 21h-8.5A3.75 3.75 0 0 1 4 17.25v-1.5Zm3.75-.75a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H7.75Zm3 0a.75.75 0 0 0 0 1.5h.008a.75.75 0 0 0 0-1.5H10.75Z" />
              </svg>
            </span>
            <span class="ls-sm__item-body">
              <span class="ls-sm__item-title">{{ $t('login.change_server') }}</span>
              <span class="ls-sm__item-sub">{{ $t('login.switch_endpoint_desc') }}</span>
            </span>
          </button>
        </div>
      </div>

      <!-- Vertically centred form content -->
      <div class="ls-right__inner">

        <!-- Mobile-only branding (left panel is hidden below 900 px) -->
        <div class="ls-mob-brand">
          <img
            :src="require('@/assets/logo/icon-geebung.png')"
            alt="Salamanda"
            class="ls-mob-brand__logo"
          />
          <span class="ls-mob-brand__name">Salamanda ERP</span>
        </div>

        <!-- Page heading -->
        <div class="ls-fhd">
          <h2 class="ls-fhd__h">{{ $t('login.title') }}</h2>
          <p class="ls-fhd__sub">{{ $t('login.welcome_heading') }}</p>
        </div>

        <!-- Auth error banner -->
        <transition name="err-slide">
          <div v-if="auth_error && auth_message" class="ls-err" role="alert">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" class="ls-err__icon">
              <path fill-rule="evenodd" d="M10 18a8 8 0 1 0 0-16 8 8 0 0 0 0 16Zm-1-11a1 1 0 1 1 2 0v4a1 1 0 1 1-2 0V7Zm1 8a1 1 0 1 0 0-2 1 1 0 0 0 0 2Z" clip-rule="evenodd" />
            </svg>
            <span>{{ auth_message }}</span>
          </div>
        </transition>

        <!-- Login form -->
        <form class="ls-form" @submit.prevent="login">

          <!-- Username -->
          <div class="ls-field">
            <label class="ls-field__lbl">{{ $t('login.username') }}</label>
            <div class="ls-field__wrap">
              <span class="ls-field__ico">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fill-rule="evenodd" d="M7.5 6a4.5 4.5 0 1 1 9 0 4.5 4.5 0 0 1-9 0ZM3.751 20.105a8.25 8.25 0 0 1 16.498 0 .75.75 0 0 1-.437.695A18.683 18.683 0 0 1 12 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 0 1-.437-.695Z" clip-rule="evenodd" />
                </svg>
              </span>
              <input
                ref="username"
                v-model.trim="data.username"
                class="ls-field__input"
                type="text"
                :placeholder="$t('login.username')"
                autocomplete="username"
                required
                @keyup.enter.prevent="focusPass"
              />
            </div>
          </div>

          <!-- Password -->
          <div class="ls-field">
            <label class="ls-field__lbl">{{ $t('login.password') }}</label>
            <div class="ls-field__wrap">
              <span class="ls-field__ico">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path fill-rule="evenodd" d="M12 1.5a5.25 5.25 0 0 0-5.25 5.25v3A3 3 0 0 0 3.75 12v6.75A3.75 3.75 0 0 0 7.5 22.5h9a3.75 3.75 0 0 0 3.75-3.75V12a3 3 0 0 0-3-3V6.75A5.25 5.25 0 0 0 12 1.5Zm3.75 8.25v-3a3.75 3.75 0 1 0-7.5 0v3h7.5Z" clip-rule="evenodd" />
                </svg>
              </span>
              <input
                ref="password"
                v-model="data.password"
                class="ls-field__input"
                :type="showPassword ? 'text' : 'password'"
                :placeholder="$t('login.password')"
                autocomplete="current-password"
                required
                @keyup.enter="login"
              />
              <button
                type="button"
                class="ls-field__pw-eye"
                :aria-label="showPassword ? 'Hide password' : 'Show password'"
                @click="showPassword = !showPassword"
              >
                <svg v-if="showPassword" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M3.53 2.47a.75.75 0 0 0-1.06 1.06l2.05 2.05A12.132 12.132 0 0 0 1.5 12c2.309 4.19 6.71 7.5 10.5 7.5a10.43 10.43 0 0 0 4.334-.988l2.136 2.136a.75.75 0 1 0 1.06-1.06l-18-18Z" />
                  <path d="M10.312 7.524 8.56 5.772A10.64 10.64 0 0 1 12 4.5c3.79 0 8.191 3.31 10.5 7.5a12.07 12.07 0 0 1-3.358 3.92l-1.564-1.564a5.25 5.25 0 0 0-7.266-6.832Z" />
                  <path d="M15.195 12.973 11.027 8.805a3.75 3.75 0 0 1 4.168 4.168Z" />
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 4.5c-3.79 0-8.191 3.31-10.5 7.5C3.809 16.19 8.21 19.5 12 19.5s8.191-3.31 10.5-7.5C20.191 7.81 15.79 4.5 12 4.5Z" />
                  <path d="M12 8.25a3.75 3.75 0 1 0 0 7.5 3.75 3.75 0 0 0 0-7.5Z" />
                </svg>
              </button>
            </div>
          </div>

          <!-- Remember me + Forgot password -->
          <div class="ls-extras">
            <label class="ls-extras__remember">
              <input v-model="rememberMe" type="checkbox" class="ls-extras__check" />
              <span class="ls-extras__remember-lbl">{{ $t('login.rememberMe') }}</span>
            </label>
            <button type="button" class="ls-extras__forgot" @click="forgotPassword">
              {{ $t('link.pw_reset') }}
            </button>
          </div>

          <!-- Submit -->
          <button
            type="submit"
            class="ls-submit"
            :disabled="!data.username || !data.password || loading"
          >
            <svg
              v-if="loading"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              class="ls-submit__spinner"
            >
              <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" style="opacity:0.25" />
              <path fill="currentColor" d="M4 12a8 8 0 0 1 8-8v4a4 4 0 0 0-4 4H4Z" style="opacity:0.75" />
            </svg>
            <span>{{ $t('login.button') }}</span>
          </button>

        </form>

        <p class="ls-right__copy">{{ $t('login.copyright') }}</p>
      </div>
    </main>

  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'

export default {
  name: 'Login',
  data () {
    return {
      data: {
        username: '',
        password: ''
      },
      rememberMe: true,
      showPassword: false,
      showServerMenu: false,
      auth_message: '',
      loginFeatures: [
        {
          key: 'inventory',
          icon: 'mdi-package-variant-closed',
          titleKey: 'login.features.inventory.title',
          hintKey: 'login.features.inventory.feature1'
        },
        {
          key: 'pos',
          icon: 'mdi-cash-register',
          titleKey: 'login.features.pos.title',
          hintKey: 'login.features.pos.feature1'
        },
        {
          key: 'accounting',
          icon: 'mdi-finance',
          titleKey: 'login.features.accounting.title',
          hintKey: 'login.features.accounting.feature1'
        },
        {
          key: 'analytics',
          icon: 'mdi-chart-line',
          titleKey: 'login.features.analytics.title',
          hintKey: 'login.features.analytics.feature1'
        }
      ]
    }
  },
  computed: {
    ...mapState({
      loading: state => state.Auth.status === 'loading',
      auth_error: state => state.Auth.status === 'error',
      name: state => state.Auth.user.name,
      last_login: state => state.Auth.user.last_login,
      auth_status: state => state.Auth.status,
      intended: state => state.Auth.intended
    })
  },
  watch: {
    auth_status (value) {
      if (value !== 'error') return
      if (!this.auth_message) this.auth_message = this.$t('login.error.message')
    }
  },
  mounted () {
    this.$nextTick(() => {
      if (this.$refs.username && this.$refs.username.focus) this.$refs.username.focus()
    })
    document.addEventListener('click', this.onDocumentClick, true)
  },
  beforeDestroy () {
    document.removeEventListener('click', this.onDocumentClick, true)
  },
  methods: {
    ...mapActions('Base', ['setBaseURL']),
    ...mapActions('Auth', ['setIntended']),
    toggleServerMenu () {
      this.showServerMenu = !this.showServerMenu
    },
    onDocumentClick (e) {
      if (!this.showServerMenu) return
      const menuRoot = this.$refs.serverMenuRoot
      if (menuRoot && menuRoot.contains && menuRoot.contains(e.target)) return
      this.showServerMenu = false
    },
    login () {
      this.auth_message = ''
      this.$authService.login(Object.assign({}, this.data))
        .then(res => {
          this.setLanguage(res.user.lang || 'en')

          const displayName = (res && res.user && res.user.name) || this.name
          const lastLogin = (res && res.user && res.user.last_login) || this.last_login
          this.$notify({
            title: this.$t('status.success'),
            message: this.$t('login.welcome', [displayName, lastLogin]),
            type: 'success'
          })

          // Always go to dashboard and clear any saved intended route,
          // so users never land on an unauthorized or permission-blocked page.
          this.setIntended(null)
          this.$router.push({ name: 'dashboard' })
        })
        .catch(err => {
          if (err && err.response) {
            const status = err.response.status
            if (status === 500) {
              this.auth_message = this.$t('login.error.server')
            } else if (status === 401) {
              this.auth_message = (err.response.data && err.response.data.error) || this.$t('login.error.message')
            } else {
              this.auth_message = this.$t('login.error.message')
            }
          } else {
            this.auth_message = this.$t('login.error.server')
          }
        })
    },
    focusPass () {
      if (this.$refs.password && this.$refs.password.focus) this.$refs.password.focus()
    },
    changeServerAddress () {
      this.showServerMenu = false
      this.setBaseURL(null)
      this.setIntended(null)
      this.$router.push({ name: 'base' })
    },
    forgotPassword () {
      this.displayMessage(this.$t('message.coming'), () => {}, 'info', this.$t('link.pw_reset'))
    },
    getOffer () {
      this.displayMessage(this.$t('message.coming'))
    }
  }
}
</script>

<style scoped>
/* ─── ROOT SHELL ────────────────────────────────────────── */
.ls {
  display: flex;
  min-height: 100vh;
  overflow: hidden;
}

/* ─── LEFT PANEL ────────────────────────────────────────── */
.ls-left {
  display: flex;
  flex-direction: column;
  width: 55%;
  position: relative;
  overflow: hidden;
  background: linear-gradient(145deg,
    #3730a3 0%,
    #4f46e5 28%,
    #6d28d9 55%,
    #2563eb 80%,
    #1d4ed8 100%
  );
}

/* ── Ambient glow orbs ── */
.ls-orb {
  position: absolute;
  border-radius: 50%;
  filter: blur(110px);
  pointer-events: none;
}
.ls-orb--a {
  width: 420px; height: 420px;
  top: -100px; left: -120px;
  background: radial-gradient(circle, rgba(255, 255, 255, 0.22) 0%, transparent 70%);
  animation: ls-float 9s ease-in-out infinite;
}
.ls-orb--b {
  width: 360px; height: 360px;
  top: 38%; right: -60px;
  background: radial-gradient(circle, rgba(192, 132, 252, 0.45) 0%, transparent 70%);
  animation: ls-float 11s ease-in-out infinite reverse;
  animation-delay: -3.5s;
}
.ls-orb--c {
  width: 300px; height: 300px;
  bottom: -30px; left: 28%;
  background: radial-gradient(circle, rgba(96, 165, 250, 0.4) 0%, transparent 70%);
  animation: ls-float 13s ease-in-out infinite;
  animation-delay: -7s;
}
@keyframes ls-float {
  0%, 100% { transform: translate(0, 0) scale(1); }
  33%       { transform: translate(20px, -24px) scale(1.05); }
  66%       { transform: translate(-14px, 16px) scale(0.96); }
}

/* ── Geometric rings ── */
.ls-ring {
  position: absolute;
  border-radius: 50%;
  pointer-events: none;
  border: 1px solid rgba(255, 255, 255, 0.15);
}
.ls-ring--1 { width: 330px;  height: 330px;  right: -85px;  bottom: -65px; }
.ls-ring--2 { width: 580px;  height: 580px;  right: -210px; bottom: -190px; }
.ls-ring--3 { width: 830px;  height: 830px;  right: -330px; bottom: -310px; }

/* ── Left inner layout ── */
.ls-left__inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px 52px;
  position: relative;
  z-index: 1;
}

/* ── Brand block ── */
.ls-brand { margin-bottom: 48px; }

.ls-brand__logo-shell {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 72px; height: 72px;
  border-radius: 20px;
  background: rgba(255, 255, 255, 0.09);
  backdrop-filter: blur(12px);
  border: 1px solid rgba(255, 255, 255, 0.14);
  margin-bottom: 22px;
}
.ls-brand__logo {
  width: 46px; height: 46px;
  object-fit: contain;
}
.ls-brand__name {
  font-size: 2.3rem;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.025em;
  line-height: 1.15;
  margin: 0 0 10px;
}
.ls-brand__accent {
  background: linear-gradient(135deg, #a78bfa, #38bdf8);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.ls-brand__tagline {
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.48);
  line-height: 1.65;
  margin: 0;
  max-width: 340px;
}

/* ── Feature list ── */
.ls-feats {
  list-style: none;
  margin: 0; padding: 0;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.ls-feat {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}
.ls-feat__icon {
  flex-shrink: 0;
  width: 40px; height: 40px;
  border-radius: 10px;
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.11);
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.78);
  transition: background 0.2s;
}
.ls-feat__icon:hover { background: rgba(255, 255, 255, 0.12); }
.ls-feat__icon i { font-size: 20px; line-height: 1; }
.ls-feat__body { display: flex; flex-direction: column; gap: 2px; }
.ls-feat__title {
  font-size: 0.865rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
}
.ls-feat__hint {
  font-size: 0.775rem;
  color: rgba(255, 255, 255, 0.38);
}

/* ── Left footer ── */
.ls-left__copy {
  padding: 18px 52px;
  font-size: 0.72rem;
  color: rgba(255, 255, 255, 0.22);
  margin: 0;
  position: relative;
  z-index: 1;
}

/* ─── RIGHT PANEL ────────────────────────────────────────── */
.ls-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  position: relative;
  overflow-y: auto;
}
/* Subtle left-edge gradient accent */
.ls-right::before {
  content: '';
  position: absolute;
  left: 0; top: 0; bottom: 0;
  width: 1px;
  background: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(99, 102, 241, 0.18) 30%,
    rgba(99, 102, 241, 0.18) 70%,
    transparent 100%
  );
}

/* ─── SERVER MENU ──────────────────────────────────────── */
.ls-sm {
  position: absolute;
  top: 18px; right: 18px;
  z-index: 10;
}
.ls-sm__btn {
  width: 38px; height: 38px;
  border-radius: 50%;
  border: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #94a3b8;
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
}
.ls-sm__btn:hover { background: #f1f5f9; color: #334155; }
.ls-sm__icon { width: 18px; height: 18px; }
.ls-sm__menu {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 256px;
  background: #ffffff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  box-shadow: 0 12px 32px rgba(0,0,0,0.09), 0 2px 8px rgba(0,0,0,0.05);
  overflow: hidden;
}
.ls-sm__item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
}
.ls-sm__item:hover { background: #f8fafc; }
.ls-sm__item-icon {
  flex-shrink: 0;
  width: 36px; height: 36px;
  border-radius: 9px;
  background: #eff6ff;
  color: #3b82f6;
  display: flex;
  align-items: center;
  justify-content: center;
}
.ls-sm__item-icon svg { width: 18px; height: 18px; }
.ls-sm__item-body { display: flex; flex-direction: column; gap: 2px; }
.ls-sm__item-title { font-size: 0.82rem; font-weight: 600; color: #1e293b; }
.ls-sm__item-sub   { font-size: 0.71rem; color: #94a3b8; }

/* ─── RIGHT INNER LAYOUT ────────────────────────────────── */
.ls-right__inner {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 56px 52px;
  max-width: 440px;
  margin: 0 auto;
  width: 100%;
  box-sizing: border-box;
}

/* Mobile brand (hidden on desktop) */
.ls-mob-brand {
  display: none;
  align-items: center;
  gap: 12px;
  margin-bottom: 40px;
}
.ls-mob-brand__logo { width: 40px; height: 40px; object-fit: contain; }
.ls-mob-brand__name {
  font-size: 1.25rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.02em;
}

/* ── Form heading ── */
.ls-fhd { margin-bottom: 28px; }
.ls-fhd__h {
  font-size: 1.85rem;
  font-weight: 800;
  color: #0f172a;
  letter-spacing: -0.025em;
  margin: 0 0 6px;
  line-height: 1.2;
}
.ls-fhd__sub {
  font-size: 0.875rem;
  color: #64748b;
  margin: 0;
  line-height: 1.55;
}

/* ── Auth error banner ── */
.ls-err {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 12px 14px;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 10px;
  font-size: 0.845rem;
  color: #991b1b;
  margin-bottom: 18px;
}
.ls-err__icon { flex-shrink: 0; width: 17px; height: 17px; margin-top: 1px; }
/* error slide transition */
.err-slide-enter-active, .err-slide-leave-active { transition: all 0.22s ease; }
.err-slide-enter, .err-slide-leave-to { opacity: 0; transform: translateY(-6px); }

/* ─── FORM ─────────────────────────────────────────────── */
.ls-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}
.ls-field { display: flex; flex-direction: column; gap: 6px; }
.ls-field__lbl {
  font-size: 0.75rem;
  font-weight: 700;
  color: #334155;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.ls-field__wrap { position: relative; }
.ls-field__ico {
  position: absolute;
  left: 14px; top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
  color: #94a3b8;
  display: flex;
}
.ls-field__ico svg { width: 18px; height: 18px; }
.ls-field__input {
  width: 100%;
  padding: 13px 14px 13px 44px;
  font-size: 0.9rem;
  color: #0f172a;
  background: #f8fafc;
  border: 1.5px solid #e2e8f0;
  border-radius: 12px;
  outline: none;
  transition: border-color 0.18s, box-shadow 0.18s, background 0.18s;
  box-sizing: border-box;
}
.ls-field__input::placeholder { color: #94a3b8; }
.ls-field__input:focus {
  border-color: #6366f1;
  background: #ffffff;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.12);
}
.ls-field__pw-eye {
  position: absolute;
  right: 12px; top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  cursor: pointer;
  color: #94a3b8;
  display: flex;
  align-items: center;
  padding: 4px;
  border-radius: 6px;
  transition: color 0.15s;
}
.ls-field__pw-eye:hover { color: #475569; }
.ls-field__pw-eye svg { width: 18px; height: 18px; }

/* ── Remember me + Forgot ── */
.ls-extras {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: -2px;
}
.ls-extras__remember {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.ls-extras__check {
  width: 15px; height: 15px;
  accent-color: #6366f1;
  cursor: pointer;
}
.ls-extras__remember-lbl {
  font-size: 0.845rem;
  color: #475569;
}
.ls-extras__forgot {
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.825rem;
  font-weight: 600;
  color: #6366f1;
  padding: 0;
  transition: color 0.15s;
}
.ls-extras__forgot:hover { color: #4f46e5; }

/* ── Submit button ── */
.ls-submit {
  width: 100%;
  padding: 14px 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  font-size: 0.9rem;
  font-weight: 700;
  color: #ffffff;
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 50%, #3730a3 100%);
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.18s, box-shadow 0.18s, opacity 0.18s;
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.38);
  letter-spacing: 0.015em;
  margin-top: 4px;
}
.ls-submit:hover:not(:disabled) {
  transform: translateY(-2px);
  box-shadow: 0 8px 28px rgba(99, 102, 241, 0.48);
}
.ls-submit:active:not(:disabled) {
  transform: translateY(0);
  box-shadow: 0 4px 16px rgba(99, 102, 241, 0.38);
}
.ls-submit:disabled {
  opacity: 0.52;
  cursor: not-allowed;
}
.ls-submit__spinner {
  width: 18px; height: 18px;
  animation: ls-spin 0.75s linear infinite;
}
@keyframes ls-spin { to { transform: rotate(360deg); } }

/* ── Right panel footer ── */
.ls-right__copy {
  margin-top: 32px;
  font-size: 0.72rem;
  color: #94a3b8;
  text-align: center;
}

/* ─── RESPONSIVE ─────────────────────────────────────────── */
@media (max-width: 900px) {
  .ls-left { display: none; }

  .ls-right {
    background: linear-gradient(160deg, #3730a3 0%, #4f46e5 45%, #2563eb 100%);
    min-height: 100vh;
  }
  .ls-right::before { display: none; }

  /* Server menu on dark background */
  .ls-sm__btn { color: rgba(255,255,255,0.55); }
  .ls-sm__btn:hover { background: rgba(255,255,255,0.08); color: #ffffff; }

  .ls-right__inner { padding: 52px 28px; }

  /* Show mobile brand */
  .ls-mob-brand { display: flex; }
  .ls-mob-brand__name { color: #ffffff; }

  /* Text colours on dark bg */
  .ls-fhd__h  { color: #ffffff; font-size: 1.6rem; }
  .ls-fhd__sub { color: rgba(255,255,255,0.5); }
  .ls-field__lbl { color: rgba(255,255,255,0.7); }

  /* Input overrides */
  .ls-field__input {
    background: rgba(255,255,255,0.055);
    border-color: rgba(255,255,255,0.13);
    color: #ffffff;
  }
  .ls-field__input::placeholder { color: rgba(255,255,255,0.28); }
  .ls-field__input:focus {
    border-color: #818cf8;
    background: rgba(255,255,255,0.08);
    box-shadow: 0 0 0 3px rgba(129,140,248,0.18);
  }
  .ls-field__ico { color: rgba(255,255,255,0.35); }
  .ls-field__pw-eye { color: rgba(255,255,255,0.35); }
  .ls-field__pw-eye:hover { color: rgba(255,255,255,0.7); }

  .ls-extras__remember-lbl { color: rgba(255,255,255,0.55); }
  .ls-extras__forgot { color: #a5b4fc; }
  .ls-extras__forgot:hover { color: #c7d2fe; }
  .ls-extras__check { accent-color: #a5b4fc; }

  .ls-right__copy { color: rgba(255,255,255,0.22); }
}

@media (max-width: 480px) {
  .ls-right__inner { padding: 40px 20px; }
  .ls-fhd__h { font-size: 1.45rem; }
}
</style>
